import React, { useEffect, useState } from "react";
import PageHeader from "../components/common/PageHeader";
import StatCard from "../components/common/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuthStore } from "../context/AuthContext";
import { getDashboardStats } from "../services/dashboardService";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, Stethoscope, Mail, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const response = await getDashboardStats();
        if (response.success) {
          setStats(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overview"
        title={`Welcome back, ${user?.first_name || "Admin"}`}
        description="Monitor your system's performance and manage your professional networks from one central hub."
        badge="Live Data"
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total Users" value={loading ? "..." : stats?.totalUsers || "0"} tone="default" />
        <StatCard label="Professors" value={loading ? "..." : stats?.totalProfessors || "0"} tone="primary" />
        <StatCard label="Doctors" value={loading ? "..." : stats?.totalDoctors || "0"} tone="info" />
        <StatCard label="Courses" value={loading ? "..." : stats?.totalCourses || "0"} tone="success" />
      </div>

      <Card className="flex flex-col h-full">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
          {[
            {
              title: "Courses",
              description: "Manage curriculum",
              url: "/courses",
              icon: BookOpen,
              color: "bg-emerald-50 text-emerald-600 border-emerald-100"
            },
            {
              title: "Professors",
              description: "Faculty management",
              url: "/professors",
              icon: GraduationCap,
              color: "bg-blue-50 text-blue-600 border-blue-100"
            },
            {
              title: "Doctors",
              description: "Staff management",
              url: "/doctors",
              icon: Stethoscope,
              color: "bg-indigo-50 text-indigo-600 border-indigo-100"
            },
            {
              title: "Contacts",
              description: "Inquiries & messages",
              url: "/contacts",
              icon: Mail,
              color: "bg-amber-50 text-amber-600 border-amber-100"
            }
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.title}
                to={item.url}
                className="group flex flex-col p-4 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
              >
                <div className={`flex items-center justify-center h-10 w-10 rounded-xl border ${item.color} mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-panel-ink group-hover:text-primary transition-colors">{item.title}</p>
                  <p className="mt-0.5 text-xs text-panel-muted">{item.description}</p>
                </div>
                <div className="mt-2 flex items-center text-[11px] font-bold tracking-wider text-primary opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-[-4px] group-hover:translate-x-0">
                  <span>Manage</span>
                  <ArrowRight size={12} className="ml-1" />
                </div>
              </Link>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

